import {
  Column,
  ColumnKeys,
  Dependencies,
  EnumValues,
  getAllPermissionsLabels,
  getAllStatusTypeLabels,
  getAllWebCategoryLabels,
  getWebCategoriesLabels,
  Permissions,
  ScheduleGridEvents,
  SchedulesWebComponentSchema,
  StatusType,
} from '../types';
import { defaultPageSizeOptions, GridViewMetadata } from '../../../grid/gridView/types';
import { Filter } from '../../../grid/gridFilters/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { WebAccessControlSchedulerGridDataProvider } from '../dataProvider/WebAccessControlSchedulerGridDataProvider';
import { html } from 'lit';
import moment from 'moment';
import { permissionsRenderer } from '../../maintenanceWindowGrid/view/CustomRenderers';
import { GridFilterUiConfiguration } from '../../../types/filters/types';
import { translateFn } from '../../../utils/translator.helper';

export class GridConfig {

  private filtersUiConfig:Array<GridFilterUiConfiguration> = [
    {
      key: ColumnKeys.LAST_MODIFIED_DATE,
      config: {
        enableSelectAllToDate: true,
      },
    },
  ];

  public getDataForView(dependencies: Dependencies): SchedulesWebComponentSchema {
    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'web-access-control-scheduler',
      filters: this.getDefaultFilters(),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.getColumnRenderers(),
      showGlobalActions: true,
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: new WebAccessControlSchedulerGridDataProvider(dependencies),
      enableCSVExport: false,
      sortColumnName: ColumnKeys.LAST_MODIFIED_DATE,
      sortDirection: 'desc',
    };

    return {
      viewMetaData,
    };
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    return [
      {
        [ColumnKeys.SCHEDULE_NAME]: {
          contains: '',
        },
      },
      {
        [ColumnKeys.STATUS]: {
          in: [],
        },
      },
      {
        [ColumnKeys.LAST_MODIFIED_DATE]: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        [ColumnKeys.LAST_EDITED_BY]: {
          contains: '',
        },
      },
      {
        [ColumnKeys.CATEGORIES_AFFECTED]: {
          in: [],
        },
      },
      {
        [ColumnKeys.PERMISSIONS]: {
          in: [],
        },
      },
    ];
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    return [
      ColumnKeys.SCHEDULE_NAME,
      ColumnKeys.SCHEDULE_TYPE,
      ColumnKeys.STATUS,
      ColumnKeys.LAST_MODIFIED_DATE,
      ColumnKeys.LAST_EDITED_BY,
      ColumnKeys.CATEGORIES_AFFECTED,
      ColumnKeys.PERMISSIONS,
    ];
  }

  private getColumns(): Column[] {
    return [
      Column.SCHEDULE_NAME,
      Column.SCHEDULE_TYPE,
      Column.STATUS,
      Column.SCHEDULE_DETAILS,
      Column.LAST_MODIFIED_DATE,
      Column.LAST_EDITED_BY,
      Column.CATEGORIES_AFFECTED,
      Column.PERMISSIONS,
      Column.IN_POLICIES,
    ];
  }

  private getColumnRenderers() {
    return {
      [ColumnKeys.SCHEDULE_NAME]: rowData => {
        return html`<gz-button class="-ml-2" lowercase type="quiet" color="primary" buttonheight="medium" buttonfontsize="small"  @click="${this.onScheduleNameCellClick.bind(this, rowData)}">${rowData.item.name}</gz-button>`;
      },
      [ColumnKeys.SCHEDULE_DETAILS]: rowData => {
        const schedules = Array.isArray(rowData.item?.subschedules) ? rowData.item?.subschedules.length : 0;
        const label = schedules === 1 ? "1 " + translateFn("Schedule") : schedules + " " + translateFn("Schedules");
        return html` <gz-grid-cell-default-renderer .label="${label}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.STATUS]: rowData => {
        const status = getAllStatusTypeLabels();
        const label = rowData.item?.inPoliciesCount >= 1 ? status[StatusType.USED] : status[StatusType.UNUSED];
        return html`<gz-grid-cell-default-renderer .label="${label}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.CATEGORIES_AFFECTED]: rowData => {
        let subscheduleCategories: Array<number> = [];
        if (Array.isArray(rowData.item?.subschedules)) {
          subscheduleCategories = rowData.item.subschedules.flatMap((subschedule) => subschedule && subschedule.categories ? subschedule.categories : undefined);
        }
        const categoriesAffected: Array<string> = getWebCategoriesLabels(subscheduleCategories);
        categoriesAffected.sort();
        return html` <gz-grid-cell-default-renderer .label="${categoriesAffected.join(", ")}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.LAST_MODIFIED_DATE]: rowData => {
        return html`<gz-grid-cell-default-renderer .label="${moment(rowData.item.updated.date).format('DD MMM YYYY, HH:mm')}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.LAST_EDITED_BY]: rowData => {
        return html` <gz-grid-cell-default-renderer .label="${rowData.item.updated.userName}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.PERMISSIONS]: permissionsRenderer,
      [ColumnKeys.IN_POLICIES]: rowData => {
        return html`<gz-button class="-ml-2" lowercase type="quiet" color="primary" buttonheight="medium" buttonfontsize="small" @click="${this.onInPoliciesCellClick.bind(this, rowData)}">${rowData.item.inPoliciesCount}</gz-button>`;
      },
    };
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.SCHEDULE_NAME,
        key: ColumnKeys.SCHEDULE_NAME,
        columnName: translateFn('Scheduler name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filterResetValue: {contains: ''},
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: this.getEnumValues(getAllStatusTypeLabels()),
        removable: false,
        sortable: false,
        filterResetValue: [],
      },
      {
        column: Column.SCHEDULE_DETAILS,
        key: ColumnKeys.SCHEDULE_DETAILS,
        columnName: translateFn('Scheduler details'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.LAST_MODIFIED_DATE,
        key: ColumnKeys.LAST_MODIFIED_DATE,
        columnName: translateFn('Last modified'),
        type: ColumnType.DATE,
        removable: false,
        sortable: true,
        showTimePickers: false,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.LAST_EDITED_BY,
        key: ColumnKeys.LAST_EDITED_BY,
        columnName: translateFn('Last edited by'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filterResetValue: {contains: ''},
      },
      {
        column: Column.CATEGORIES_AFFECTED,
        key: ColumnKeys.CATEGORIES_AFFECTED,
        columnName: translateFn('Categories affected'),
        type: ColumnType.ENUM,
        values: this.getEnumValues(getAllWebCategoryLabels()),
        removable: false,
        sortable: false,
        filterResetValue: [],
      },
      {
        column: Column.PERMISSIONS,
        key: ColumnKeys.PERMISSIONS,
        columnName: translateFn('Permissions'),
        type: ColumnType.ENUM,
        values: this.getEnumValues(getAllPermissionsLabels()),
        removable: false,
        sortable: true,
        filterResetValue: {
          in: [Permissions.SHARED, Permissions.NOT_SHARED],
        },
      },
      {
        column: Column.IN_POLICIES,
        key: ColumnKeys.IN_POLICIES,
        columnName: translateFn('In policies'),
        type: ColumnType.NUMBER,
        removable: false,
        sortable: true,
        filteringDisabled: true,
      },
    ];
  }

  public getEnumValues(keyValueMap: Record<number, string>): EnumValues[] {
    const labels: EnumValues[] = [];

    for (const [value, label] of Object.entries(keyValueMap)) {
      labels.push({
        label: label,
        value: Number(value),
      });
    }
    return labels.sort((a, b) => a.label.localeCompare(b.label));
  }

  private onScheduleNameCellClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(new CustomEvent(ScheduleGridEvents.CLICK_ON_SCHEDULE_NAME, {
      bubbles: true,
      composed: true,
      detail: {
        rowData: rowData,
      },
    }));
  }

  private onInPoliciesCellClick(rowData, event: CustomEvent) {
    (event.target as HTMLTextAreaElement)?.getRootNode()?.dispatchEvent(new CustomEvent(ScheduleGridEvents.CLICK_ON_IN_POLICIES, {
      bubbles: true,
      composed: true,
      detail: {
        companyId: rowData.item.company.id,
        schedule: {
          _id: rowData.item._id,
          name: rowData.item.name,
        },
      },
    }));
  }

}
